import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Search, UserPlus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EmployeePayroll from "@/components/EmployeePayroll";
import OrgChart from "@/components/OrgChart";
import AttendancePage from "@/pages/AttendancePage";
import LeavesPage from "@/pages/LeavesPage";
import RegularizationsPage from "@/pages/RegularizationsPage";
import PayslipsPage from "@/pages/PayslipsPage";
import { Separator } from "@/components/ui/separator";

function initials(name = "") {
  return name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();

  // "active" or "exited". Exited staff are fetched deliberately so they never
  // leak into the working list.
  const [view, setView] = useState("active");
  const [tab, setTab] = useState("roster");
  const [rejoining, setRejoining] = useState(null);

  const doRejoin = async (e) => {
    const when = window.prompt(
      "Rejoining " + e.name + ".\n\nThis starts a fresh tenure: probation runs again from " +
      "the new joining date and leave is allocated for the current year. " +
      "The earlier spell is kept in their history.\n\nJoining date (YYYY-MM-DD):",
      new Date().toISOString().slice(0, 10)
    );
    if (!when) return;
    setRejoining(e.id);
    try {
      await api.post(`/employees/${e.id}/rejoin`, { join_date: when.trim() });
      toast.success(e.name + " is active again");
      setView("active");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRejoining(null); }
  };


  const load = async () => {
    try {
      const { data } = await api.get("/employees", {
        params: { ...(q ? { q } : {}), status: view === "exited" ? "inactive" : "active" },
      });
      setEmployees(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [view, q]);

  return (
    <div className="space-y-6">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-100">
          <TabsTrigger value="roster" data-testid="emp-tab-roster">People</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="payroll" data-testid="emp-tab-payroll">Payroll</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="org" data-testid="emp-tab-org">Org chart</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="attendance" data-testid="emp-tab-attendance">Attendance</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="leaves" data-testid="emp-tab-leaves">Leaves</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="regs" data-testid="emp-tab-regs">Regularizations</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="payslips" data-testid="emp-tab-payslips">Payslips</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="roster" className="space-y-6 mt-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">{user?.role !== "admin" && user?.manages ? "My Team" : "Employees"}</h1>
          <p className="mt-1 text-sm text-zinc-500">{employees.length} {employees.length === 1 ? "person" : "people"}</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 rounded-md" data-testid="add-employee-btn">
                <UserPlus className="h-4 w-4 mr-2" /> Add employee
              </Button>
            </DialogTrigger>
            <AddEmployeeDialog onClose={() => { setOpen(false); load(); }} />
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-1 mb-3">
        {[["active", "Current"], ["exited", "Exited"]].map(([v, label]) => (
          <button key={v} type="button" onClick={() => setView(v)}
            data-testid={`view-${v}`}
            className={`h-8 px-3 rounded-md text-sm ${view === v
              ? "bg-zinc-900 text-white"
              : "text-zinc-600 hover:bg-zinc-100"}`}>
            {label}
          </button>
        ))}
      </div>
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-4 border-b border-zinc-100">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, code" className="pl-9 h-9 rounded-md border-zinc-300" data-testid="employee-search-input" />
          </div>
        </CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Employee</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Code</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Department</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Designation</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Monthly pay</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Status</TableHead>
                {isAdmin && <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => (
                <TableRow key={e.id} onClick={() => navigate(`/employees/${e.id}`)} className="border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer" data-testid={`employee-row-${e.employee_code}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center text-xs font-semibold">{initials(e.name)}</div>
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{e.name}</div>
                        <div className="text-xs text-zinc-500">{e.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600">{e.employee_code}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[11px] capitalize ${e.employment_type === "intern" ? "border-amber-200 text-amber-700 bg-amber-50" : "border-emerald-200 text-emerald-700 bg-emerald-50"}`}>
                      {e.employment_type === "intern" ? "Intern" : "Full-time"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-600">{e.department || "—"}</TableCell>
                  <TableCell className="text-sm text-zinc-600">{e.designation || "—"}</TableCell>
                  {view === "exited" ? (
                      <TableCell className="text-sm text-zinc-600">
                        <div>{e.last_working_day || "—"}</div>
                        <div className="text-xs text-zinc-500">{e.exit_reason || "No reason recorded"}</div>
                      </TableCell>
                    ) : (
                      <TableCell className="text-sm text-right tabular-nums text-zinc-800">
                    {monthlyPay(e) ? `₹ ${monthlyPay(e).toLocaleString("en-IN")}` : "—"}
                  </TableCell>
                    )}
                  <TableCell>
                    <Badge className={e.status === "active" ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50" : "bg-zinc-100 text-zinc-700 border border-zinc-200 hover:bg-zinc-100"}>
                      {e.status}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={(ev) => { ev.stopPropagation(); setEditing(e); }} className="h-8" data-testid={`edit-employee-${e.employee_code}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                        {view === "exited" && (
                          <Button size="sm" variant="ghost" disabled={rejoining === e.id}
                            onClick={(ev) => { ev.stopPropagation(); doRejoin(e); }}
                            data-testid={`rejoin-${e.employee_code}`}
                            className="h-8 text-xs text-emerald-700">
                            {rejoining === e.id ? "Working..." : "Rejoin"}
                          </Button>
                        )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!employees.length && (
                <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-sm text-zinc-500 py-10">No employees yet. Add your first teammate.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {isAdmin && editing && (
        <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
          <EditEmployeeDialog employee={editing} employees={employees} onClose={(refresh) => { setEditing(null); if (refresh) load(); }} />
        </Dialog>
      )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="payroll" className="mt-4">
            <EmployeePayroll />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="org" className="mt-4">
            <OrgChart />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="attendance" className="mt-4">
            <AttendancePage embedded />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="leaves" className="mt-4">
            <LeavesPage embedded />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="regs" className="mt-4">
            <RegularizationsPage embedded />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="payslips" className="mt-4">
            <PayslipsPage embedded />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function monthlyPay(e) {
  const s = e.salary || {};
  if (e.employment_type === "intern") {
    return Math.round((s.monthly_stipend || 0) + (s.incentives || 0));
  }
  return Math.round((s.basic || 0) + (s.hra || 0) + (s.special_allowance || 0));
}

function AddEmployeeDialog({ onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", department: "", designation: "", role: "employee", employment_type: "full_time", ctc_annual: "", employee_code: "", pan: "", aadhaar: "", bank_account: "", bank_ifsc: "", bank_name: "", account_holder_name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        // Anyone can be made a manager: picking them here is what grants the
          // rights, so the list is not limited to people already flagged.
          setManagers(data.filter((e) => (e.status || "active") === "active"));
      } catch {}
    })();
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    // client-side quick check for bank fields
    if (form.bank_account && !form.account_holder_name.trim()) {
      toast.error("Account holder name is required when bank account is provided");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        ctc_annual: parseFloat(form.ctc_annual || 0),
        manager_id: managerId || null,
        employee_code: form.employee_code ? form.employee_code.toUpperCase() : null,
        bank_ifsc: form.bank_ifsc ? form.bank_ifsc.toUpperCase() : null,
      };
      const { data } = await api.post("/employees", payload);
      toast.success(`Added ${data.employee.name} (${data.employee.employee_code}). Password: ${data.default_password || "provided"}`);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 gap-0">
      <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100 shrink-0">
        <DialogTitle className="font-display text-xl">Add employee</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="flex flex-col min-h-0 flex-1" data-testid="add-employee-form">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs text-zinc-600">Full name</Label><Input required value={form.name} onChange={change("name")} className="h-9" data-testid="input-name" /></div>
            <div className="col-span-2"><Label className="text-xs text-zinc-600">Work email</Label><Input type="email" required value={form.email} onChange={change("email")} className="h-9" data-testid="input-email" /></div>
            <div><Label className="text-xs text-zinc-600">Phone</Label><Input value={form.phone} onChange={change("phone")} className="h-9" /></div>
            <div><Label className="text-xs text-zinc-600">Department</Label><Input value={form.department} onChange={change("department")} className="h-9" /></div>
            <div><Label className="text-xs text-zinc-600">Designation</Label><Input value={form.designation} onChange={change("designation")} className="h-9" /></div>
            <div>
              <Label className="text-xs text-zinc-600">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-600">Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                <SelectTrigger className="h-9" data-testid="input-employment-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full-Time</SelectItem>
                  <SelectItem value="intern">Intern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-600">Reporting manager</Label>
              <Select value={managerId} onValueChange={setManagerId}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select manager (optional)" /></SelectTrigger>
                <SelectContent>
                  {managers.map((m) => <SelectItem key={m.id} value={m.user_id}>{m.name} — {m.employee_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs text-zinc-600">{form.employment_type === "intern" ? "Annual stipend (₹)" : "Annual CTC (₹)"}</Label><Input type="number" value={form.ctc_annual} onChange={change("ctc_annual")} className="h-9" placeholder={form.employment_type === "intern" ? "300000" : "900000"} /></div>
            <div>
              <Label className="text-xs text-zinc-600">Employee code</Label>
              <Input value={form.employee_code} onChange={change("employee_code")} className="h-9 uppercase" placeholder="Auto: ARB###" maxLength={16} data-testid="input-employee-code" />
            </div>
            <div><Label className="text-xs text-zinc-600">PAN</Label><Input value={form.pan} onChange={change("pan")} className="h-9" /></div>
            <div><Label className="text-xs text-zinc-600">Aadhaar</Label><Input value={form.aadhaar} onChange={change("aadhaar")} className="h-9" /></div>
            <div><Label className="text-xs text-zinc-600">Bank name</Label><Input value={form.bank_name} onChange={change("bank_name")} className="h-9" /></div>
            <div>
              <Label className="text-xs text-zinc-600">Bank A/C {form.bank_account && <span className="text-red-500">*</span>}</Label>
              <Input value={form.bank_account} onChange={change("bank_account")} className="h-9" placeholder="9–18 digits" pattern="\d{9,18}" maxLength={18} data-testid="input-bank-account" />
            </div>
            <div>
              <Label className="text-xs text-zinc-600">IFSC</Label>
              <Input value={form.bank_ifsc} onChange={change("bank_ifsc")} className="h-9 uppercase" placeholder="HDFC0001234" pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}" maxLength={11} data-testid="input-bank-ifsc" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-600">Account holder name {form.bank_account && <span className="text-red-500">*</span>}</Label>
              <Input value={form.account_holder_name} onChange={change("account_holder_name")} className="h-9" placeholder="As per bank records" required={!!form.bank_account} data-testid="input-account-holder" />
              <p className="mt-1 text-[11px] text-zinc-500">Beneficiary name exactly as per bank records — required when bank account is filled.</p>
            </div>
          </div>
        </div>
        <DialogFooter className="px-6 py-3 border-t border-zinc-100 bg-white shrink-0">
          <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-add-employee">
            {submitting ? "Adding…" : "Add employee"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}


function fmtINR(n) {
  return `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function EditEmployeeDialog({ employee, employees, onClose }) {
  const [form, setForm] = useState({
    name: employee.name || "",
    phone: employee.phone || "",
    department: employee.department || "",
    designation: employee.designation || "",
    employment_type: employee.employment_type || "full_time",
    status: employee.status || "active",
    employee_code: employee.employee_code || "",
    pan: employee.pan || "",
    aadhaar: employee.aadhaar || "",
    bank_account: employee.bank_account || "",
    bank_ifsc: employee.bank_ifsc || "",
    bank_name: employee.bank_name || "",
    account_holder_name: employee.account_holder_name || "",
    manager_id: employee.manager_id || "",
  });
  const [salary, setSalary] = useState({
    basic: employee.salary?.basic || 0,
    hra: employee.salary?.hra || 0,
    special_allowance: employee.salary?.special_allowance || 0,
    deductions_manual: employee.salary?.deductions_manual || 0,
    monthly_stipend: employee.salary?.monthly_stipend || 0,
    incentives: employee.salary?.incentives || 0,
  });
  const [saving, setSaving] = useState(false);
  const isIntern = form.employment_type === "intern";
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const changeSalary = (k) => (e) => setSalary({ ...salary, [k]: parseFloat(e.target.value || 0) });

  const gross = (salary.basic || 0) + (salary.hra || 0) + (salary.special_allowance || 0);
  const deductions = salary.deductions_manual || 0;
  const netFullTime = gross - deductions;
  const internPayout = (salary.monthly_stipend || 0) + (salary.incentives || 0);

  const save = async (e) => {
    e.preventDefault();
    if (form.bank_account && !form.account_holder_name.trim()) {
      toast.error("Account holder name is required when bank account is provided");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        employee_code: form.employee_code ? form.employee_code.toUpperCase() : null,
        bank_ifsc: form.bank_ifsc ? form.bank_ifsc.toUpperCase() : null,
        salary: {
          ctc_annual: isIntern ? internPayout * 12 : gross * 12,
          basic: salary.basic, hra: salary.hra, special_allowance: salary.special_allowance,
          deductions_manual: salary.deductions_manual,
          monthly_stipend: salary.monthly_stipend, incentives: salary.incentives,
        },
      };
      await api.patch(`/employees/${employee.id}`, payload);
      toast.success("Employee updated");
      onClose(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
      <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100 shrink-0">
        <DialogTitle className="font-display text-xl">Edit {employee.name}</DialogTitle>
      </DialogHeader>
      <form onSubmit={save} className="flex flex-col min-h-0 flex-1">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        <Tabs defaultValue="profile">
          <TabsList className="bg-transparent p-0 border-b border-zinc-200 rounded-none h-auto w-full justify-start">
            <TabsTrigger value="profile" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="edit-tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="salary" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="edit-tab-salary">Salary structure</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs text-zinc-600">Full name</Label><Input value={form.name} onChange={change("name")} className="h-9" /></div>
              <div><Label className="text-xs text-zinc-600">Phone</Label><Input value={form.phone} onChange={change("phone")} className="h-9" /></div>
              <div><Label className="text-xs text-zinc-600">Date of birth</Label><Input type="date" value={form.dob || ""} onChange={change("dob")} className="h-9" data-testid="input-dob" /></div>
              <div><Label className="text-xs text-zinc-600">Department</Label><Input value={form.department} onChange={change("department")} className="h-9" /></div>
              <div><Label className="text-xs text-zinc-600">Designation</Label><Input value={form.designation} onChange={change("designation")} className="h-9" /></div>
              <div>
                <Label className="text-xs text-zinc-600">Employment type</Label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                  <SelectTrigger className="h-9" data-testid="edit-employment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full-Time</SelectItem>
                    <SelectItem value="intern">Intern</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-600">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-600">Manager</Label>
                <Select value={form.manager_id || "none"} onValueChange={(v) => setForm({ ...form, manager_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9" data-testid="edit-manager"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No manager</SelectItem>
                    {(employees || []).filter((e) => e.id !== employee.id).map((e) => (
                      <SelectItem key={e.id} value={e.user_id}>{e.name} ({e.employee_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs text-zinc-600">PAN</Label><Input value={form.pan} onChange={change("pan")} className="h-9" /></div>
              <div><Label className="text-xs text-zinc-600">Aadhaar</Label><Input value={form.aadhaar} onChange={change("aadhaar")} className="h-9" /></div>
              <div><Label className="text-xs text-zinc-600">Bank name</Label><Input value={form.bank_name} onChange={change("bank_name")} className="h-9" /></div>
              <div>
                <Label className="text-xs text-zinc-600">Bank A/C</Label>
                <Input value={form.bank_account} onChange={change("bank_account")} className="h-9" placeholder="9–18 digits" pattern="\d{9,18}" maxLength={18} data-testid="edit-bank-account" />
              </div>
              <div>
                <Label className="text-xs text-zinc-600">IFSC</Label>
                <Input value={form.bank_ifsc} onChange={change("bank_ifsc")} className="h-9 uppercase" placeholder="HDFC0001234" pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}" maxLength={11} data-testid="edit-bank-ifsc" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-zinc-600">Account holder name {form.bank_account && <span className="text-red-500">*</span>}</Label>
                <Input value={form.account_holder_name} onChange={change("account_holder_name")} className="h-9" placeholder="As per bank records" data-testid="edit-account-holder" />
                <p className="mt-1 text-[11px] text-zinc-500">Beneficiary name exactly as per bank records — required when bank account is filled.</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="salary" className="mt-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-3">
              {isIntern ? "Intern payout structure (monthly, ₹)" : "Full-time salary structure (monthly, ₹)"}
            </div>

            {isIntern ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-600">Monthly Stipend</Label>
                    <Input type="number" min="0" step="1" value={salary.monthly_stipend} onChange={changeSalary("monthly_stipend")} className="h-9 tabular-nums" data-testid="salary-monthly-stipend" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Incentives (optional)</Label>
                    <Input type="number" min="0" step="1" value={salary.incentives} onChange={changeSalary("incentives")} className="h-9 tabular-nums" data-testid="salary-incentives" />
                  </div>
                </div>
                <Separator />
                <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3">
                  <div className="text-sm font-medium text-emerald-900">Total Monthly Payout</div>
                  <div className="font-display text-2xl font-bold text-emerald-700 tabular-nums" data-testid="salary-total-payout">{fmtINR(internPayout)}</div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium">Earnings</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-600">Basic Pay</Label>
                    <Input type="number" min="0" step="1" value={salary.basic} onChange={changeSalary("basic")} className="h-9 tabular-nums" data-testid="salary-basic" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">HRA</Label>
                    <Input type="number" min="0" step="1" value={salary.hra} onChange={changeSalary("hra")} className="h-9 tabular-nums" data-testid="salary-hra" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600">Special Allowance</Label>
                    <Input type="number" min="0" step="1" value={salary.special_allowance} onChange={changeSalary("special_allowance")} className="h-9 tabular-nums" data-testid="salary-special" />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-md bg-zinc-50 border border-zinc-200 px-4 py-2.5">
                  <div className="text-sm font-medium text-zinc-700">Gross Earnings</div>
                  <div className="font-display text-lg font-semibold text-zinc-900 tabular-nums" data-testid="salary-gross">{fmtINR(gross)}</div>
                </div>

                <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-medium pt-2">Deductions</div>
                <div>
                  <Label className="text-xs text-zinc-600">TDS / PF / Other</Label>
                  <Input type="number" min="0" step="1" value={salary.deductions_manual} onChange={changeSalary("deductions_manual")} className="h-9 tabular-nums" data-testid="salary-deductions" />
                </div>
                <div className="flex items-center justify-between rounded-md bg-zinc-50 border border-zinc-200 px-4 py-2.5">
                  <div className="text-sm font-medium text-zinc-700">Total Deductions</div>
                  <div className="font-display text-lg font-semibold text-zinc-900 tabular-nums" data-testid="salary-total-deductions">{fmtINR(deductions)}</div>
                </div>

                <Separator />
                <div className="flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3">
                  <div className="text-sm font-medium text-emerald-900">Net Take-Home Pay</div>
                  <div className="font-display text-2xl font-bold text-emerald-700 tabular-nums" data-testid="salary-net-takehome">{fmtINR(netFullTime)}</div>
                </div>
              </div>
            )}
            <p className="mt-3 text-[11px] text-zinc-500">All amounts are in INR, shown monthly. Editable by admin only.</p>
          </TabsContent>
        </Tabs>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-zinc-100 bg-white shrink-0">
          <Button type="button" variant="outline" onClick={() => onClose(false)} className="border-zinc-300">Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="edit-employee-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
